import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { PageMasterMode, usePageContext } from "../navigation/PageContext";
import { SessionMode } from "../../contexts/appStore/hooks";
import { BaseTopBar } from "./BaseTopBar";
import React, { useCallback } from "react";
import { isExpert } from "../../helpers/roles";
import { useAppStoreDispatch } from "../../contexts/appStore/context";
import { isSpectator, useAuthContext, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { useMeetingContainerContext } from "../../contexts/meetingContainer/context";
import { sessionIsEphemeral } from "../../helpers/session";
import { NavigateGlobalQuery } from "../../helpers/navigation";
import { useSettings } from "../../hooks/settings";

export interface MasterTopBarProps {
  onSettings?: () => void;
  onBack?: () => void;
  backIcon?: "arrow" | "exit";
  settingsBtnRef?: React.Ref<HTMLDivElement>;
  meetingTimerRef?: React.Ref<HTMLDivElement>;
  onMeetingTimerClick?: () => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  /** Shown when no live meeting provides one (a replay). */
  meetingName?: string;
}

export const MasterTopBar: React.FC<MasterTopBarProps> = React.memo(({
  onSettings,
  onBack,
  backIcon,
  settingsBtnRef,
  meetingTimerRef,
  onMeetingTimerClick,
  isFullscreen,
  onToggleFullscreen,
  meetingName: fallbackMeetingName,
}) => {
  const dispatch = useAppStoreDispatch();
  const storedUser = useAppStoreSelector(state => state.user);
  const { status: authStatus, logout } = useAuthContext();
  const user = authStatus === "in" ? storedUser : undefined;

  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);

  const [settings] = useSettings();

  const containerCtx = useMeetingContainerContext({ raiseIfUndefined: false });

  const [meetingId] = useMeetingSelector(containerCtx?.containerAPI, "meeting", meeting => meeting?.id);
  const [liveMeetingName] = useMeetingSelector(containerCtx?.containerAPI, "meeting", meeting => meeting?.name);
  const meetingName = liveMeetingName ?? fallbackMeetingName;
  const [meetingStartedAt] = useMeetingSelector(containerCtx?.containerAPI, "meeting", meeting => meeting?.startedAt);
  const [meetingState] = useMeetingSelector(containerCtx?.containerAPI, "meeting", meeting => meeting?.state);
  // A stopped course keeps its `startedAt`: only show the elapsed timer while
  // the course is actually ongoing (paused included), never on IDLE/STOPPED.
  const meetingOngoing = meetingState === MeetingState.RUNNING || meetingState === MeetingState.PAUSED;
  const [watchingNumber] = useMeetingSelector(containerCtx?.containerAPI, "sessions", sessions => sessions?.filter(s => !isExpert(s)).length);
  const [session] = useMeetingSelector(containerCtx?.containerAPI, "session");

  const disableMeetingList = settings?.ui?.disableMeetingList === true;

  const page = pageContext?.page;
  
  // A page-provided onBack always wins. The default back never applies to
  // home-like pages (LOGIN has none, LIST's own back is the replay overlay
  // close). With the meeting list disabled the lobby joins them: it is then
  // the top of the world — no back at all (the page passes none either).
  const hideDefaultBack = [PageMasterMode.LIST, PageMasterMode.LOGIN].includes(page!!)
    || (disableMeetingList && page === PageMasterMode.LOBBY);

  const defaultOnBack = useCallback(() => {
    // Ephemeral (guest) sessions have no meeting list to return to. Leaving
    // means dropping the session: clearing the tokens swaps to the public route
    // tree, which redirects the current /meeting/:id URL to the meeting lobby —
    // no explicit navigation needed.
    if (sessionIsEphemeral()) {
      logout();
      return;
    }
    if (disableMeetingList) {
      // No-list mode: leaving is an exit toward the meeting lobby, never a
      // go-to-previous. `replace` keeps the meeting out of the browser history
      // AND out of PageContext's previous location, so no back can re-enter it.
      const id = meetingId ?? pageContextRef.current?.params?.id;
      if (id) {
        pageContextRef.current?.goTo(PageMasterMode.LOBBY, { id }, { replace: true, forwardParams: [NavigateGlobalQuery.PROVIDER] });
      } else {
        // No meeting in scope (e.g. /logout): land on the "join" screen.
        pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { replace: true });
      }
    } else {
      pageContextRef.current?.goToPrevious(PageMasterMode.LIST);
    }
    if (meetingId) {
      dispatch("setLastLeftMeetingId", meetingId);
    }
  }, [dispatch, logout, meetingId, disableMeetingList, pageContextRef]);

  return (
    <BaseTopBar
      user={user}
      meetingName={meetingName}
      meetingStartedAt={meetingOngoing ? meetingStartedAt : undefined}
      showDate={!meetingName} // show date if no meeting
      showLogo={true}
      showFullscreenButton={settings?.ui?.showFullscreenButton}
      isFullscreen={isFullscreen}
      onToggleFullscreen={onToggleFullscreen}
      onSettings={onSettings}
      onBack={onBack ?? (hideDefaultBack ? undefined : defaultOnBack)}
      // In no-list mode the default back is an exit (guests included: their
      // logout drops them on the meeting lobby). An explicit onBack keeps the
      // classic arrow unless the page passes its own backIcon.
      backIcon={backIcon ?? (onBack || !disableMeetingList ? undefined : "exit")}
      watchingNumber={watchingNumber}
      isSpectator={!!session && isSpectator(session)}
      isExpert={!!session && isExpert(session)}
      settingsBtnRef={settingsBtnRef}
      meetingTimerRef={meetingTimerRef}
      onMeetingTimerClick={onMeetingTimerClick}
    />
  );
});